import { clone, cloneDeep } from 'lodash-es';

import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

import {
    ReadContextRoleScopeRequestDTO,
    ReadContextRoleScopeResponseDTO,
    ReadUserContextRoleInternalRequestRequestDTO,
    ReadUserContextRoleInternalRequestResponseDTO,
    ReadUserRequestDTO,
    ReadUserResponseDTO,
    ResendUserContextRoleRequestRequestDTO,
    ResendUserContextRoleRequestResponseDTO,
    SaveUserContextRoleRequestDTO,
    SaveUserRoleContextExternalRequestRequestDTO,
    SaveUserRoleContextExternalRequestResponseDTO,
    SaveUserContextRoleResponseDTO,
    TypeScopeDTO,
    UserAccountInformationDTO,
    UserContextRoleDTO,
    UserContextRoleScopeDTO,
    UserContextRoleScopeFieldDTO,
    ReadUserContextRoleExternalRequestResponseDTO,
    ReadUserContextRoleExternalRequestRequestDTO,
    SaveUserRoleContextInternalRequestResponseDTO,
    SaveUserRoleContextInternalRequestRequestDTO,
    SaveUserContextRoleProfileRequestDTO,
    SaveUserContextRoleProfileResponseDTO,
    PageRequestDTO,
    CheckUserEmailResponseDTO,
    ReadMetaRequestDTO,
} from './dtos';
import { DataTableComponent } from './components';
import { AnyKey } from './view';
import { ContextRoleService, GlobalsService, MetaService, UsersService } from './services';
import { endWith, Observable } from 'rxjs';
import { ConfirmationService } from 'primeng/api';

export declare type Nullable<T = void> = T | null | undefined;

export type IDBTableStyleType =
    | {
          [klass: string]: any;
      }
    | null
    | undefined;
export const IDBTableStyle: IDBTableStyleType = { 'min-width': '50rem', 'table-layout': 'fixed' };

export type IDBGetStringType = (item: any) => string;
export const IDBGetString: IDBGetStringType = (item: any) => '';

export function parseNumber(str: string | undefined): number | undefined {
    if (!isEmpty(str)) {
        return Number(str);
    }

    return undefined;
}

export function replaceEmailExt(email: string | undefined): string | undefined {
    if (!isEmpty(email)) {
        return email!.replace(/.com/g, '.alskdjrueiwoqp');
    }

    return email;
}

export function encodeURIComponentFull(str: string | undefined): string | undefined {
    if (!isEmpty(str)) {
        return encodeURIComponent(str!).replace(/[!'()*.-]/g, (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase());
    }

    return str;
}

export function decodeURIComponentFull(str: string | undefined): string | undefined {
    if (!isEmpty(str)) {
        return decodeURIComponent(str!);
    }

    return str;
}

export const FilterDelayDefault = 2000;
export function doRun(run: () => void, time: number = 0, timeOut: any = undefined): any {
    if (isAssigned(timeOut)) {
        clearTimeout(timeOut);
    }

    return setTimeout(() => run(), time);
}

export function clearTimer(timeOut: any): any {
    if (isAssigned(timeOut)) {
        clearTimeout(timeOut);
    }

    return undefined;
}

export interface ChangedEvent {
    hasChanged: boolean;
    isValidated: boolean;
}

export function isAssigned(value: any): boolean {
    return value !== undefined && value !== null;
}

export function isEmpty(val: string | undefined): boolean {
    return !isAssigned(val) || val === '';
}

export function formatUserSearchLabel(userEmail?: string, userFullName?: string): string {
    if (isEmpty(userEmail)) {
        return userFullName ?? '';
    }
    if (isEmpty(userFullName)) {
        return userEmail!;
    }
    return `${userEmail} (${userFullName})`;
}

export function blobToString(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    const xmlRequest = new XMLHttpRequest();
    xmlRequest.open('GET', url, false);
    xmlRequest.send();
    URL.revokeObjectURL(url);
    return xmlRequest.responseText;
}

export const DefaultDateFormat = 'MM/dd/yyyy hh:mm a';
export const DateFormatYearFirst = 'yyyy/MM/dd hh:mm a';

export function userContextRoleScopeToTypeScope(
    contextRole: UserContextRoleDTO,
    scope: UserContextRoleScopeDTO | undefined,
    changeOnly = false,
    withSuffix = true,
): TypeScopeDTO | undefined {
    if (isAssigned(scope) && (!changeOnly || scope?.hasChanged === true || scope?.isDeleted === true)) {
        const suffix = withSuffix ? 'Value' : '';
        const approvedProfiles: string[] = [];
        const deniedProfiles: string[] = [];
        const res: TypeScopeDTO = {};
        let fi = 1;

        contextRole.scopeFields?.forEach((sf) => {
            if (sf.fieldType === 'text' && fi <= 4) {
                const fks = `${sf.fieldMapping}${suffix}` as AnyKey;
                const fkd = `field${fi}Value` as AnyKey;
                const fv = (scope as any)[fks];

                if (isAssigned(fv) && fv !== '') {
                    (res as any)[fkd] = fv;
                }

                fi++;
            } else if (sf.fieldType === 'switch') {
                const fks = sf.fieldMapping as AnyKey;
                const sfv = (((scope as any)[fks] ?? '') as string).toUpperCase();

                if (isAssigned(sf.fieldCd)) {
                    if (sfv.startsWith('APPROVED')) {
                        approvedProfiles.push(sf.fieldCd ?? '');
                    } else if (sfv.startsWith('DENIED')) {
                        deniedProfiles.push(sf.fieldCd ?? '');
                    }
                }
            }
        });

        res.approvedProfiles = approvedProfiles.join();
        res.deniedProfiles = deniedProfiles.join();

        return res;
    }

    return undefined;
}

export function userContextRoleScopesToTypeScopes(
    contextRole: UserContextRoleDTO,
    scopes: UserContextRoleScopeDTO[] | undefined,
    changesOnly = false,
    withSuffix = true,
): TypeScopeDTO[] | undefined {
    if (isAssigned(scopes) && (scopes?.length ?? 0) > 0) {
        const res: TypeScopeDTO[] = [];

        scopes?.forEach((s) => {
            const scope = userContextRoleScopeToTypeScope(contextRole, s, changesOnly, withSuffix);

            if (isAssigned(scope)) {
                res.push(scope!);
            }
        });

        return res;
    }

    return undefined;
}

const cleanAllValue = (s: string | undefined) => {
    return s?.toLowerCase() !== 'all' ? s : undefined;
};

export function clearAllInTypeScope(ts: TypeScopeDTO | undefined) {
    if (isAssigned(ts)) {
        ts!.field1Value = cleanAllValue(ts!.field1Value);
        ts!.field2Value = cleanAllValue(ts!.field2Value);
        ts!.field3Value = cleanAllValue(ts!.field3Value);
        ts!.field4Value = cleanAllValue(ts!.field4Value);
    }
}

export function clearAllInTypeScopes(tss: TypeScopeDTO[] | undefined) {
    tss?.forEach((ts) => {
        clearAllInTypeScope(ts);
    });
}

export function firstOrDefault<T>(a: T[] | undefined): T | undefined {
    const l = a?.length ?? 0;
    if (isAssigned(a) && l > 0) {
        return a![0];
    }

    return undefined;
}

export function lastOrDefault<T>(a: T[] | undefined): T | undefined {
    const l = a?.length ?? 0;
    if (isAssigned(a) && l > 0) {
        return a![l - 1];
    }

    return undefined;
}

export function generateScopes(contextRole: UserContextRoleDTO, newScopes: UserContextRoleScopeDTO[] | undefined): TypeScopeDTO[] | undefined {
    const scopes: UserContextRoleScopeDTO[] = [];

    if (isAssigned(newScopes) && (newScopes?.length ?? 0) > 0) {
        newScopes
            ?.filter((s) => s.hasChanged === true)
            ?.forEach((s) => {
                scopes.push(s);
            });
    }

    const res = userContextRoleScopesToTypeScopes(contextRole, scopes);

    return (res?.length ?? 0) > 0 ? res : undefined;
}

export function getIsValidClass(isValid: boolean): string {
    return isValid ? '' : ' idb-invalid ng-invalid ng-dirty';
}

const regexEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export function isEmailValid(email: string): boolean {
    return regexEmail.test(email);
}

export function emailPattern(): string {
    return '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';
}

export function localStorageRemoveItemsWithPrefix(prefix: string): void {
    const keysToRemove = Object.keys(localStorage).filter((key) => key.startsWith(prefix));

    keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
    });
}

export const ErrorMessageSettings = {
    timeOut: 30000,
};

export function showErrorMessage(toastr: ToastrService, globals: GlobalsService, message?: string, getMessage?: (message?: string) => string): boolean {
    let finalMsg = message;
    if (!message?.startsWith('SPMSG:')) {
        const url = location.href;
        const userName = `${globals.currentUser?.userName}&lt;${globals.currentUser?.userEmail}&gt;`;
        const now = new Date();
        const utcString =
            String(now.getUTCDate()).padStart(2, '0') +
            '-' +
            String(now.getUTCMonth() + 1).padStart(2, '0') +
            '-' +
            now.getUTCFullYear() +
            ' ' +
            String(now.getUTCHours()).padStart(2, '0') +
            ':' +
            String(now.getUTCMinutes()).padStart(2, '0') +
            ':' +
            String(now.getUTCSeconds()).padStart(2, '0');
        const titleMsg =
            'Application generated error. Please contact support at <a href="mailto:extranetsupport@iadb.org">extranetsupport@iadb.org</a> or try again later.';

        if (message?.startsWith('WARN:')) {
            message = message.substring(5).trim();
        }

        finalMsg = `${titleMsg}<div class="toast-details">Message: ${message}<br />URL: ${url}<br />User: ${userName}<br />UTC: ${utcString}</div>`;
    } else {
        finalMsg = message.substring(6).trim();
    }

    if (!isEmpty(message) && !isEmpty(finalMsg)) {
        if (isAssigned(getMessage)) {
            toastr.error(getMessage!(message), undefined, ErrorMessageSettings);
        } else {
            toastr.error(finalMsg, undefined, ErrorMessageSettings);
        }
        return true;
    }

    return false;
}

export function showResponseErrorMessage(toastr: ToastrService, globals: GlobalsService, response: any, getMessage?: (message?: string) => string): boolean {
    const message = response?.title ?? response?.message ?? response?.errorMessage;

    return showErrorMessage(toastr, globals, message, getMessage);
}

export function filterAssignedData<T>(data: T): T {
    if (typeof data !== 'object' || data === null) {
        return data;
    }

    if (Array.isArray(data)) {
        return data.map((item) => filterAssignedData(item)) as any;
    }

    return Object.keys(data)
        .filter((key) => {
            const keyv = key as keyof typeof data;
            return data[keyv] !== null && data[keyv] !== undefined;
        })
        .reduce((obj, key) => {
            const keyv = key as keyof typeof data;
            obj[key] = filterAssignedData(data[keyv]);
            return obj;
        }, {} as any) as T;
}

export function joinWithAnd(items?: string[], separator = ', ', andLabel = 'and'): string {
    const l = items?.length ?? 0;

    if (l === 1) {
        return items![0];
    } else if (l === 2) {
        return `${items![0]} ${andLabel} ${items![1]}`;
    } else if (l >= 3) {
        const inits = clone(items!);
        const last = inits.pop();

        return `${inits.join(separator)} ${andLabel} ${last}`;
    }

    return '';
}

export function updateFields(
    contextRole: UserContextRoleDTO,
    oldScopeFields: UserContextRoleScopeFieldDTO[],
    newScopeFields: UserContextRoleScopeFieldDTO[],
    oldScopes: UserContextRoleScopeDTO[],
    newScopes: UserContextRoleScopeDTO[],
    onNewScope: (scope: UserContextRoleScopeDTO) => void = () => {},
): void {
    const tmpSF = cloneDeep(oldScopeFields);
    newScopeFields?.forEach((sf) => {
        const sfcd = sf.fieldCd?.toLowerCase();
        let crsf = oldScopeFields.find((e) => e.fieldCd?.toLowerCase() === sfcd);

        if (!isAssigned(crsf)) {
            let sfm = 0;
            tmpSF.forEach((e) => {
                const sfo = e.fieldOrder ?? -1;
                sfm = sfo > sfm ? sfo : sfm;
            });
            sfm++;

            crsf = {
                userRoleContextId: contextRole.userRoleContextId,
                contextCd: contextRole.contextCd,
                roleCd: contextRole.roleCd,
                fieldCd: sf.fieldCd,
                fieldName: sf.fieldName,
                fieldType: sf.fieldType,
                fieldMapping: `field${sfm}`,
                fieldOrder: sfm,
                canEditProfileColumnAction: sf.canEditProfileColumnAction,
                isRequired: sf.isRequired,
                canAddMultipleScopes: sf.canAddMultipleScopes,
            };

            tmpSF.push(crsf);
        }
    });

    contextRole.scopeFields = tmpSF;

    newScopes?.forEach((s) => {
        let ncrs: UserContextRoleScopeDTO = {
            userRoleContextId: contextRole.userRoleContextId,
            contextCd: contextRole.contextCd,
            roleCd: contextRole.roleCd,
            canEditProfileRowAction: s.canEditProfileRowAction,
            canDeleteScopeRowAction: s.canDeleteScopeRowAction,
        };

        newScopeFields?.forEach((sf) => {
            const sfcd = sf.fieldCd?.toLowerCase();
            let crsf = tmpSF.find((e) => e.fieldCd?.toLowerCase() === sfcd);
            (ncrs as any)[crsf?.fieldMapping as AnyKey] = (s as any)[sf.fieldMapping as AnyKey];
            (ncrs as any)[(crsf?.fieldMapping + 'Value') as AnyKey] = (s as any)[(sf.fieldMapping + 'Value') as AnyKey];
            (ncrs as any)[(crsf?.fieldMapping + 'Id') as AnyKey] = (s as any)[(sf.fieldMapping + 'Id') as AnyKey];
            (ncrs as any)[(crsf?.fieldMapping + 'CanApprove') as AnyKey] = (s as any)[(sf.fieldMapping + 'CanApprove') as AnyKey];
            (ncrs as any)[(crsf?.fieldMapping + 'HasAccessDetails') as AnyKey] = (s as any)[(sf.fieldMapping + 'HasAccessDetails') as AnyKey];
            (ncrs as any)[(crsf?.fieldMapping + 'DisableAutoUpdates') as AnyKey] = (s as any)[(sf.fieldMapping + 'DisableAutoUpdates') as AnyKey];
            (ncrs as any)[(crsf?.fieldMapping + 'DisableAutoUpdatesLabel') as AnyKey] = (s as any)[(sf.fieldMapping + 'DisableAutoUpdatesLabel') as AnyKey];
        });

        onNewScope(ncrs);
        oldScopes.push(ncrs);
    });
}

export function doSaveUserContextRole(
    toastr: ToastrService,
    globals: GlobalsService,
    usersSrv: UsersService,
    onLoading: (isLoading: boolean) => void,
    onFinish: (response: SaveUserContextRoleResponseDTO) => void,
    translate: (key: string) => string,
    accountInfo?: UserAccountInformationDTO,
    contextRole?: UserContextRoleDTO,
    scopes?: UserContextRoleScopeDTO[],
    request: SaveUserContextRoleRequestDTO = {},
) {
    const req: any = {};

    req.contextCd = request.contextCd ?? contextRole?.contextCd;
    req.roleCd = request.roleCd ?? contextRole?.roleCd;
    req.userEmail = request.userEmail ?? contextRole?.information?.userEmail;
    req.userId = request.userId ?? contextRole?.information?.userId;
    req.canCreateAccounts = request.canCreateAccounts ?? contextRole?.information?.canCreateAccounts;
    req.canReceiveApprovalNotifications = request.canReceiveApprovalNotifications ?? contextRole?.information?.canReceiveApprovalNotifications;
    req.firstName = request.firstName ?? contextRole?.information?.firstName;
    req.middleName = request.middleName ?? contextRole?.information?.middleName;
    req.lastName = request.lastName ?? contextRole?.information?.lastName;
    req.preferredLanguageCd = request.preferredLanguageCd ?? contextRole?.information?.preferredLanguageCd;
    req.fromDt = request.fromDt ?? contextRole?.information?.roleGrantedFromDate;
    req.toDt = request.toDt ?? contextRole?.information?.roleGrantedToDate;
    req.deleteRole = request.deleteRole;

    if (req.fromDt instanceof Date) {
        req.fromDt = req.fromDt?.toISOString();
    } else {
        req.fromDt = req.fromDt ?? '1900-01-01';
    }

    if (req.toDt instanceof Date) {
        req.toDt = req.toDt?.toISOString();
    } else {
        req.toDt = req.toDt ?? '1900-01-01';
    }

    if (accountInfo?.isInternal !== true) {
        req.secondaryEmail = request.secondaryEmail ?? contextRole?.information?.secondaryEmail;
        req.primaryPhone = request.primaryPhone ?? contextRole?.information?.primaryPhone;
        req.mobilePhone = request.mobilePhone ?? contextRole?.information?.mobilePhone;
        req.fax = request.fax ?? contextRole?.information?.fax;
    }

    if (req.deleteRole !== true) {
        req.scopes = userContextRoleScopesToTypeScopes(contextRole ?? { userRoleContextId: -1 }, scopes ?? contextRole?.scopes, true);
        clearAllInTypeScopes(req.scopes);
    }

    req.isInternal = request.isInternal ?? accountInfo?.isInternal;

    onLoading(true);

    usersSrv.saveUserContextRole(req).subscribe({
        next: (response) => {
            onLoading(false);

            toastr.success(translate('changesSaved'));

            onFinish(response);
        },
        error: (error) => {
            onLoading(false);

            const res =
                'The record successfully saved to database but failed to update B2C account. Support was already notified. Please try to update account later using refresh icon.';

            showResponseErrorMessage(toastr, globals, error);
        },
    });
}

export function doSaveUserContextRoleRequest(
    toastr: ToastrService,
    globals: GlobalsService,
    usersSrv: UsersService,
    callExternal: boolean,
    onLoading: (isLoading: boolean) => void,
    onFinish: (response: SaveUserRoleContextExternalRequestResponseDTO | SaveUserRoleContextInternalRequestResponseDTO) => void,
    translate: (key: string) => string,
    accountInfo?: UserAccountInformationDTO,
    contextRole?: UserContextRoleDTO,
    showToaster: boolean = true,
    request: SaveUserRoleContextExternalRequestRequestDTO | SaveUserRoleContextInternalRequestRequestDTO = {},
) {
    const req: SaveUserRoleContextExternalRequestRequestDTO | SaveUserRoleContextInternalRequestRequestDTO = {};

    req.contextCd = request.contextCd ?? contextRole?.contextCd;
    req.roleCd = request.roleCd ?? contextRole?.roleCd;
    req.userEmail = request.userEmail ?? contextRole?.information?.userEmail;
    req.firstName = request.firstName ?? contextRole?.information?.firstName;
    req.middleName = request.middleName ?? contextRole?.information?.middleName;
    req.lastName = request.lastName ?? contextRole?.information?.lastName;
    req.preferredLanguageCd = request.preferredLanguageCd ?? contextRole?.information?.preferredLanguageCd;
    req.deleteRole = request.deleteRole;

    if (accountInfo?.isInternal !== true) {
        req.secondaryEmail = request.secondaryEmail ?? contextRole?.information?.secondaryEmail;
        req.primaryPhone = request.primaryPhone ?? contextRole?.information?.primaryPhone;
        req.mobilePhone = request.mobilePhone ?? contextRole?.information?.mobilePhone;
        req.fax = request.fax ?? contextRole?.information?.fax;
    }

    const scps = cloneDeep(contextRole?.scopes);
    scps?.forEach((s) => {
        s.hasChanged = true;
    });
    req.scopes = userContextRoleScopesToTypeScopes(contextRole ?? { userRoleContextId: -1 }, scps, true);
    clearAllInTypeScopes(req.scopes);

    if (isAssigned((request as any)?.deleteRole)) {
        (req as any).deleteRole = (request as any).deleteRole;
    }

    onLoading(true);

    const obs: Observable<SaveUserRoleContextExternalRequestResponseDTO> | Observable<SaveUserRoleContextInternalRequestResponseDTO> = callExternal
        ? usersSrv.saveUserContextRoleExternalRequest(req)
        : usersSrv.saveUserContextRoleInternalRequest(req);

    obs.subscribe({
        next: (response) => {
            onLoading(false);

            if (showToaster) {
                toastr.success(translate('changesSaved'));
            }

            onFinish(response);
        },
        error: (error) => {
            onLoading(false);

            showResponseErrorMessage(toastr, globals, error);
        },
    });
}

export function doReadContextRoleScope(
    toastr: ToastrService,
    globals: GlobalsService,
    contextRoleSrv: ContextRoleService,
    onLoading: (isLoading: boolean) => void,
    onFinish: (response: ReadContextRoleScopeResponseDTO) => void,
    onError: (message: string) => void,
    contextRole?: UserContextRoleDTO,
    scopes?: TypeScopeDTO[],
    usage?: string,
) {
    const request: ReadContextRoleScopeRequestDTO = {
        userEmail: contextRole?.information?.userEmail,
        scope: scopes ?? [],
        usage: usage,
        contextCd: contextRole?.contextCd,
        roleCd: contextRole?.roleCd,
        languageCd: globals.language,
    };
    clearAllInTypeScopes(request.scope);

    onLoading(true);

    contextRoleSrv.readContextRoleScope(request).subscribe({
        next: (response) => {
            onLoading(false);
            onFinish(response);
        },
        error: (error) => {
            onLoading(false);

            if (isAssigned(onError) && !isEmpty(error.message)) {
                onError!(error.message!);
            }

            showResponseErrorMessage(toastr, globals, error);
        },
    });
}

export function reloadPage(router: Router) {
    const url = router.url;
    const tempUrl = `/blank`;

    router.navigateByUrl(tempUrl, { skipLocationChange: true }).then(() => {
        router.navigate([url]);
    });
}

export function serializeToBase64(obj: any): string {
    const jsonString = JSON.stringify(obj);
    const base64String = btoa(jsonString);
    return base64String;
}

export function deserializeFromBase64(base64String: string | undefined): any {
    if (!isEmpty(base64String)) {
        const jsonString = atob(base64String!);
        const obj = JSON.parse(jsonString);
        return obj;
    }

    return undefined;
}

export function toISODateString(date: Date | undefined): string | undefined {
    if (isAssigned(date)) {
        const dstr = date!.toISOString();

        return dstr.substring(0, 10);
    }

    return undefined;
}

const NumberExpr = /^[0-9]$/;
export function isNumber(key: string): boolean {
    return NumberExpr.test(key);
}

export function toLinesHtml(str?: string, tag = 'span'): string | undefined {
    if (!isEmpty(str)) {
        const lines = str!.split('\n');
        const newLines = lines.map((line) => `<${tag}>${line}</${tag}>`);

        return newLines.join();
    }

    return str;
}

export function doReadUserContextRoleExternalRequest(
    toastr: ToastrService,
    globals: GlobalsService,
    userSrv: UsersService,
    requestKey: string,
    onLoading: (isLoading: boolean) => void,
    onFinish: (response: ReadUserContextRoleExternalRequestResponseDTO) => void,
    onError?: (errorMessage: string) => void,
): void {
    onLoading(true);

    const request: ReadUserContextRoleExternalRequestRequestDTO = {
        requestKey: requestKey,
    };

    userSrv.readUserContextRoleExternalRequest(request).subscribe({
        next: (response) => {
            onLoading(false);

            onFinish(response);
        },
        error: (error) => {
            onLoading(false);

            if (isAssigned(onError) && !isEmpty(error.message)) {
                onError!(error.message!);
            }

            showResponseErrorMessage(toastr, globals, error);
        },
    });
}

export function doReadUserContextRoleInternalRequest(
    toastr: ToastrService,
    globals: GlobalsService,
    userSrv: UsersService,
    userEmail: string,
    userRoleContextId: number | undefined,
    onLoading: (isLoading: boolean) => void,
    onFinish: (response: ReadUserContextRoleInternalRequestResponseDTO) => void,
    onError?: (errorMessage: string) => void,
): void {
    onLoading(true);

    const request: ReadUserContextRoleInternalRequestRequestDTO = {
        userEmail: userEmail,
        userRoleContextId: userRoleContextId,
        languageCd: globals.language,
    };

    userSrv.readUserContextRoleInternalRequest(request).subscribe({
        next: (response) => {
            onLoading(false);

            onFinish(response);
        },
        error: (error) => {
            onLoading(false);

            if (isAssigned(onError) && !isEmpty(error.message)) {
                onError!(error.message!);
            }

            showResponseErrorMessage(toastr, globals, error);
        },
    });
}

export function doResendEmailValidationLink(
    toastr: ToastrService,
    globals: GlobalsService,
    userSrv: UsersService,
    userEmail: string,
    requestKey: string,
    onLoading: (isLoading: boolean) => void,
    onFinish: (response: ResendUserContextRoleRequestResponseDTO) => void,
    onError?: (errorMessage: string) => void,
): void {
    onLoading(true);

    const request: ResendUserContextRoleRequestRequestDTO = {
        requestKey: requestKey,
        userEmail: userEmail,
        languageCd: globals.language,
    };

    userSrv.resendUserContextRoleRequest(request).subscribe({
        next: (response) => {
            onLoading(false);

            onFinish(response);
        },
        error: (error) => {
            onLoading(false);

            if (isAssigned(onError) && !isEmpty(error.message)) {
                onError!(error.message!);
            }

            showResponseErrorMessage(toastr, globals, error);
        },
    });
}

export function doReadUser(
    toastr: ToastrService,
    globals: GlobalsService,
    userSrv: UsersService,
    userId: number,
    contextCd: string | undefined,
    roleCd: string | undefined,
    userRoleContextId: number | undefined,
    onLoading: (isLoading: boolean) => void,
    onFinish: (response: ReadUserResponseDTO) => void,
    onError?: (errorMessage: string) => void,
) {
    onLoading(true);

    const request: ReadUserRequestDTO = {
        userId: userId,
        userRoleContextId: userRoleContextId,
        languageCd: globals.language,
    };

    if (!isEmpty(roleCd)) request.roleCd = roleCd;
    if (!isEmpty(contextCd)) request.contextCd = contextCd;

    userSrv.readUser(request).subscribe({
        next: (response) => {
            onLoading(false);

            onFinish(response);
        },
        error: (error) => {
            onLoading(false);

            if (isAssigned(onError) && !isEmpty(error.message)) {
                onError!(error.message!);
            }

            showResponseErrorMessage(toastr, globals, error);
        },
    });
}

export function doSaveUserContextRoleProfile(
    toastr: ToastrService,
    globals: GlobalsService,
    contextRoleSrv: ContextRoleService,
    request: SaveUserContextRoleProfileRequestDTO,
    onLoading: (loading: boolean) => void,
    onFinish: (response: SaveUserContextRoleProfileResponseDTO) => void,
    translate: (key: string) => string,
) {
    onLoading(true);

    request.languageCd = globals.language;

    contextRoleSrv.saveUserContextRoleProfile(request).subscribe({
        next: (response) => {
            onLoading(false);

            toastr.success(translate('changesSaved'));
            onFinish(response);
        },
        error: (error) => {
            onLoading(false);

            showResponseErrorMessage(toastr, globals, error);
        },
    });
}

export function getDeletedScopes(
    scopeFields?: UserContextRoleScopeFieldDTO[],
    existingScopes?: UserContextRoleScopeDTO[],
    newScopes?: UserContextRoleScopeDTO[],
): UserContextRoleScopeDTO[] {
    const textFields = scopeFields?.filter((sf) => sf.fieldType?.toLowerCase() === 'text') ?? [];
    let scopes: UserContextRoleScopeDTO[] = [];

    existingScopes?.filter((sE) => {
        const sN = newScopes?.find((ss2) => {
            let sfc = 0;

            textFields.forEach((sf) => {
                const sfk = `${sf.fieldMapping}Value` as AnyKey;
                if ((sE as any)[sfk] === (ss2 as any)[sfk]) {
                    sfc++;
                }
            });

            return sfc === textFields.length;
        });

        if (!isAssigned(sN)) {
            const s1c = cloneDeep(sE);
            s1c.isDeleted = true;
            scopes.push(s1c);
        }
    });

    return scopes;
}

export interface ConfirmActionOptions {
    header: string;
    message: string;
    acceptLabel: string;
    cancelLabel: string;
    icon?: string;
    acceptIcon?: string;
    cancelIcon?: string;
    acceptButtonStyle?: string;
    cancelButtonStyle?: string;
    onAccept: () => void;
    onCancel?: () => void;
}

export function confirmAction(confirmSrv: ConfirmationService, options: ConfirmActionOptions) {
    confirmSrv.confirm({
        header: options.header,
        icon: options.icon ?? 'pi pi-question-circle',
        message: options.message,
        // Setting Accept and Cancel options inverted, so the buttons can be shown inverted on screen.
        rejectLabel: options.acceptLabel,
        rejectIcon: (options.acceptIcon ?? 'pi pi-check') + ' mr-1',
        rejectButtonStyleClass: options.acceptButtonStyle ?? 'p-button-success',
        acceptLabel: options.cancelLabel,
        acceptIcon: (options.cancelIcon ?? 'pi pi-times') + ' mr-1',
        acceptButtonStyleClass: options.cancelButtonStyle ?? 'p-button-danger',
        defaultFocus: 'accept',
        closeOnEscape: false,
        blockScroll: true,
        reject: () => options.onAccept(),
        accept: () => {
            if (isAssigned(options.onCancel)) options.onCancel!();
        },
    });
}

export function fillTableSortedColumns(table: DataTableComponent, request: PageRequestDTO) {
    if (!isEmpty(table.sortField)) {
        request.sortBy = table.sortField;

        if (isAssigned(table.sortOrder) && table.sortOrder !== 0) {
            request.order = table.sortOrder == 1 ? 'asc' : 'desc';
        }
    }
}

export function doCheckUserEmail(
    metaSrv: MetaService,
    userEmail: string,
    onLoading: (isLoading: boolean) => void,
    onFinish: (userId: number | undefined) => void,
) {
    const catefory = 'emailLookup';
    const request: ReadMetaRequestDTO = {
        categories: catefory,
        filterText: userEmail?.trim(),
    };

    onLoading(true);

    metaSrv.readMeta(request).subscribe({
        next: (response) => {
            const res = isAssigned(response?.meta) ? (response.meta[catefory] ?? []) : [];
            onLoading(false);

            if (res.length > 0) {
                const user = res[0];
                const userId = parseInt(user.val ?? '', 10);

                onFinish(userId);
            } else {
                onFinish(undefined);
            }
        },
        error: () => {
            onFinish(undefined);
        },
    });
}
